import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { auth as authApi } from '../utils/api';
import { eAppNativa, accessiSocialiVisibili } from '../utils/appNativa';

const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const MICROSOFT_TENANT_ID = process.env.REACT_APP_MICROSOFT_TENANT_ID || 'common';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" style={{ flexShrink: 0 }}>
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

const btnSocialStyle = {
  width: '100%', padding: '10px 0', borderRadius: 'var(--radius-base)',
  border: '1px solid var(--ds-border)', background: 'var(--ds-bg)',
  fontSize: 12, fontWeight: 500, color: 'var(--ds-text)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaEmail, setMfaEmail] = useState('');
  // Rispedizione del link MFA. Il backend risponde SEMPRE generico
  // (anti-enumeration, SEC-019) e ha un throttle dedicato: qui basta un
  // riscontro a schermo e un'attesa che impedisca di martellarlo.
  const [reinvio, setReinvio] = useState('');      // '' | 'invio' | 'fatto' | 'errore'
  const [attesaReinvio, setAttesaReinvio] = useState(0);

  const reinviaLink = async () => {
    if (reinvio === 'invio' || attesaReinvio > 0) return;
    setReinvio('invio');
    try {
      await authApi.sendMagicLink(mfaEmail);
      setReinvio('fatto');
      setAttesaReinvio(30);
    } catch {
      setReinvio('errore');
    }
  };

  useEffect(() => {
    if (attesaReinvio <= 0) return undefined;
    const t = setTimeout(() => setAttesaReinvio(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [attesaReinvio]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result && result.mfa_required) {
        setMfaPending(true);
        setMfaEmail(result.email || email);
      } else {
        navigate('/');
      }
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      if (data?.mfa_required) {
        setMfaPending(true);
        setMfaEmail(data.email || email);
      } else if (status === 401) {
        // 401 = credenziali errate. Copy semplice per l'artigiano +
        // anti-enumeration: non esponiamo il testo tecnico del backend
        // ("Nessun account attivo trovato con queste credenziali").
        setError('Email o password non corretti');
      } else {
        // 400 account bloccato (non_field_errors) / 429 throttle (detail) /
        // altro: mostra il messaggio del backend, con fallback generico.
        setError(
          data?.detail ||
          data?.non_field_errors?.[0] ||
          'Accesso non riuscito. Riprova tra qualche istante.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    const redirect_uri = `${window.location.origin}/auth/callback`;

    if (provider === 'google' && GOOGLE_CLIENT_ID) {
      const params = new URLSearchParams({
        client_id: GOOGLE_CLIENT_ID,
        redirect_uri,
        response_type: 'code',
        scope: 'openid email profile',
        state: 'google',
        access_type: 'online',
        prompt: 'select_account',
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    }

    if (provider === 'microsoft' && MICROSOFT_CLIENT_ID) {
      // PKCE: genera code_verifier e code_challenge per Azure
      const buf = new Uint8Array(32);
      crypto.getRandomValues(buf);
      const codeVerifier = btoa(String.fromCharCode(...buf))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
      const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);

      const params = new URLSearchParams({
        client_id: MICROSOFT_CLIENT_ID,
        redirect_uri,
        response_type: 'code',
        scope: 'openid email profile User.Read',
        state: 'microsoft',
        prompt: 'select_account',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });
      window.location.href = `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}/oauth2/v2.0/authorize?${params}`;
    }
  };

  // D1 = A (01/11): nell'app solo email e password; nel sito come prima.
  const social = accessiSocialiVisibili({
    googleClientId: GOOGLE_CLIENT_ID,
    microsoftClientId: MICROSOFT_CLIENT_ID,
    appNativa: eAppNativa(),
  });
  const showSocial = social.google || social.microsoft;

  // ── MFA pending: mostra messaggio email ───────────────────────
  if (mfaPending) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--chip)'
      }}>
        <div style={{ width: '100%', maxWidth: 400, padding: '0 16px', textAlign: 'center' }}>
          <div className="gradient-brand" style={{ width: 52, height: 52, borderRadius: 'var(--radius-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
            <span style={{ color: 'white', fontWeight: 500, fontSize: 22, fontStyle: 'italic' }}>m</span>
          </div>
          <div style={{
            background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)',
            padding: 28, boxShadow: '0 6px 18px rgba(35,38,47,0.08)'
          }}>
            <Mail size={48} strokeWidth={1.5} color="var(--brand)" style={{ margin: '0 auto 16px', display: 'block' }} />
            <h2 style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink)', marginBottom: 8, marginTop: 0, fontFamily: 'var(--font-display)' }}>
              Controlla la tua email
            </h2>
            <p style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6, marginBottom: 20 }}>
              Abbiamo inviato un link di verifica a <strong>{mfaEmail}</strong>.
              Clicca il link nell'email per completare l'accesso.
            </p>
            <p style={{ fontSize: 12, color: 'var(--ds-muted)', lineHeight: 1.6 }}>
              Di solito arriva in pochi secondi, ma può metterci qualche minuto.
              Se non la vedi, guarda anche nello spam.
              <br />Il link scade tra 10 minuti.
            </p>

            {/* Il link dura 10 minuti e l'email puo' non arrivare (ritardo, spam,
                o un invio fallito che il backend non riporta al chiamante). Senza
                questo bottone l'unica uscita era rifare login e password. */}
            <div style={{ marginTop: 14 }}>
              <button
                onClick={reinviaLink}
                disabled={reinvio === 'invio' || attesaReinvio > 0}
                style={{
                  padding: '8px 20px', borderRadius: 'var(--radius-base)',
                  border: '1px solid var(--ds-border)', background: 'transparent',
                  fontSize: 13, fontWeight: 500,
                  color: (reinvio === 'invio' || attesaReinvio > 0) ? 'var(--ds-muted)' : 'var(--brand)',
                  cursor: (reinvio === 'invio' || attesaReinvio > 0) ? 'default' : 'pointer',
                }}
              >
                {reinvio === 'invio'
                  ? 'Invio in corso...'
                  : attesaReinvio > 0
                    ? `Invia di nuovo tra ${attesaReinvio}s`
                    : 'Non hai ricevuto l’email? Invia di nuovo'}
              </button>
              {reinvio === 'fatto' && (
                <p style={{ fontSize: 12, color: 'var(--brand)', marginTop: 8, marginBottom: 0 }}>
                  Link inviato di nuovo. Controlla anche la posta indesiderata.
                </p>
              )}
              {reinvio === 'errore' && (
                <p style={{ fontSize: 12, color: 'var(--danger, var(--ink))', marginTop: 8, marginBottom: 0 }}>
                  Non sono riuscito a inviare il link. Riprova fra qualche istante.
                </p>
              )}
            </div>

            <button
              onClick={() => { setMfaPending(false); setError(''); setReinvio(''); setAttesaReinvio(0); }}
              style={{
                marginTop: 16, padding: '8px 20px', borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-border)', background: 'transparent',
                fontSize: 13, color: 'var(--ds-muted)', cursor: 'pointer',
              }}
            >
              Torna al login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--chip)'
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>

        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
          <div className="gradient-brand" style={{ width: 52, height: 52, borderRadius: 'var(--radius-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
            <span style={{ color: 'white', fontWeight: 500, fontSize: 22, fontStyle: 'italic' }}>m</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>mAIstro</div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 2 }}>Artigiani</div>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)',
          padding: 28, boxShadow: '0 6px 18px rgba(35,38,47,0.08)'
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginBottom: 6, marginTop: 0, fontFamily: 'var(--font-display)' }}>Accedi al tuo account</h2>
          <p style={{ fontSize: 12, color: 'var(--ds-muted)', marginBottom: 20, marginTop: 0 }}>Inserisci le tue credenziali per continuare</p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-text)', display: 'block', marginBottom: 6 }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                required
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
                  background: 'var(--chip)', fontSize: 12, color: 'var(--ds-text)', boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-text)', display: 'block', marginBottom: 6 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
                    background: 'var(--chip)', fontSize: 12, color: 'var(--ds-text)', boxSizing: 'border-box'
                  }}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex',
                }}>
                  {showPw ? (
                    <Eye size={16} strokeWidth={2} color="var(--ds-muted)" />
                  ) : (
                    <EyeOff size={16} strokeWidth={2} color="var(--ds-muted)" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" style={{ fontSize: 11, color: 'var(--ds-danger)', background: tint('var(--ds-danger)', 8), borderRadius: 'var(--radius-base)', padding: '8px 12px' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="gradient-brand"
              style={{
                width: '100%', padding: '11px 0', borderRadius: 'var(--radius-base)', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 500, color: 'white', marginTop: 4, opacity: loading ? 0.7 : 1,
                boxShadow: '0 2px 10px rgba(35,38,47,0.06)'
              }}
            >
              {loading ? 'Accesso in corso…' : 'Accedi'}
            </button>
          </form>

          {showSocial && (
            <>
              {/* Divider */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0',
              }}>
                <div style={{ flex: 1, height: 1, background: 'var(--ds-border)' }} />
                <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>oppure</span>
                <div style={{ flex: 1, height: 1, background: 'var(--ds-border)' }} />
              </div>

              {/* Social buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {social.google && (
                  <button
                    type="button"
                    onClick={() => handleSocialLogin('google')}
                    style={btnSocialStyle}
                  >
                    <GoogleIcon /> Continua con Google
                  </button>
                )}
                {social.microsoft && (
                  <button
                    type="button"
                    onClick={() => handleSocialLogin('microsoft')}
                    style={btnSocialStyle}
                  >
                    <MicrosoftIcon /> Continua con Microsoft
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ds-muted)', marginTop: 16 }}>
          Non hai un account?{' '}
          <button
            type="button"
            onClick={() => navigate('/auth/registrati-email')}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)' }}
          >
            Registrati
          </button>
        </p>
        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ds-muted)', marginTop: 8 }}>
          <button
            type="button"
            onClick={() => navigate('/auth/password-dimenticata')}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)' }}
          >
            Password dimenticata?
          </button>
        </p>
      </div>
    </div>
  );
}
